import * as Sentry from '@sentry/react';
import {
  useLocation,
  useNavigationType,
  matchRoutes,
  createRoutesFromChildren,
} from 'react-router';
import {RootState} from '../store';
import {isChunkLoadError} from './chunkReload';

// True when the event has stack frames and every one of them points at the
// document itself or at a script with no URL — see the beforeSend filter.
// SDK 10's PII_HEADER_SNIPPETS (gone from 11): header, cookie and query keys
// dropped unless sendDefaultPii was on. Keys naming tokens, sessions or auth
// are filtered by the SDK regardless.
const PII_SNIPPETS = ['forwarded', '-ip', 'remote-', 'via', '-user'];

const isFromInjectedScript = (event: Sentry.ErrorEvent) => {
  const withFrames = (event.exception?.values ?? []).filter(
    (e) => e.stacktrace?.frames?.length,
  );
  if (!withFrames.length) return false;
  const pageUrl = window.location.origin + window.location.pathname;
  return withFrames.every((e) =>
    e.stacktrace!.frames!.every((frame) => {
      const file = frame.filename?.split(/[?#]/)[0];
      return !file || file === 'undefined' || file === pageUrl;
    }),
  );
};

// Function to configure Sentry. Call this when your app mounts
export const configure = () => {
  // The E2E smoke suite builds with VITE_SENTRY_DISABLED=true so that
  // test-induced errors — especially the intentional crash probes that verify
  // the gate — never reach Sentry as real issues (this is what created
  // SMARTMIXIN-WEB-3B). The DSN below is hardcoded, so this build-time flag is
  // the only source-level opt-out. Vite statically replaces the env reference,
  // so Sentry.init is tree-shaken out of the smoke bundle entirely.
  if (import.meta.env.VITE_SENTRY_DISABLED === 'true') {
    return;
  }

  Sentry.init({
    dsn: 'https://0dfa4e58c58c4965b2a771a60ffe812b@o76219.ingest.sentry.io/5507134',
    release: process.env.SENTRY_RELEASE,
    environment: process.env.SENTRY_ENV,
    // Facebook's in-app browser injects its own scripts under iabjs://; their
    // errors ("Java object is gone", SMARTMIXIN-WEB-56) are not ours.
    denyUrls: [/^iabjs:\/\//],
    // Sentry 11 dropped sendDefaultPii and collects every category by default,
    // the client IP included (userInfo). This is SDK 10's sendDefaultPii=false
    // mapping spelled out, so nothing more is collected than before the upgrade.
    dataCollection: {
      userInfo: false,
      cookies: {deny: PII_SNIPPETS},
      httpHeaders: {deny: PII_SNIPPETS},
      urlQueryParams: {deny: PII_SNIPPETS},
      httpBodies: [],
      genAI: {inputs: false, outputs: false},
      databaseQueryData: false,
      frameContextLines: 7,
    },
    // Off by default before Sentry 11: on, every captureMessage issue would
    // regroup under a new fingerprint.
    attachStacktrace: false,
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useLocation,
        useNavigationType,
        matchRoutes,
        createRoutesFromChildren,
      }),
    ],
    beforeSend(event) {
      // Filter out ApiError - these are expected errors (network issues, 4xx)
      // already handled by useApiClient which displays alerts to the user
      if (event.exception?.values?.some((e) => e.type === 'ApiError')) {
        return null;
      }

      // Filter out stale-chunk failures after a deploy (SMARTMIXIN-WEB-39):
      // expected churn self-healed by a hard reload (see lib/chunkReload), not
      // an application bug. Catches occurrences arriving outside the router
      // boundary (window.onerror / Vite preload).
      if (event.exception?.values?.some((e) => isChunkLoadError(e.value))) {
        return null;
      }

      // Filter out Safari's modulepreload-polyfill noise (SMARTMIXIN-WEB-3D).
      // Safari <17 lacks native <link rel=modulepreload>, so Vite injects a
      // polyfill that fire-and-forgets `fetch()` on each preload link with no
      // .catch(). A failed speculative preload (stale chunk after a deploy,
      // mid-deploy asset gap, or a fetch aborted by navigating away) surfaces as
      // an unhandled rejection with Safari's generic "Load failed" message. The
      // preload is only a cache-warming optimisation — the real import() runs on
      // its own (chunkReload-guarded) path — so this is non-actionable churn.
      // We disable the polyfill in vite.config (build.modulePreload.polyfill),
      // which stops new builds emitting it; this guard also drops the same noise
      // from older builds still cached in users' tabs. Scoped to unhandled
      // rejections so genuinely thrown "Load failed" errors still report.
      if (
        event.exception?.values?.some(
          (e) =>
            e.mechanism?.type === 'onunhandledrejection' &&
            e.value?.includes('Load failed'),
        )
      ) {
        return null;
      }

      // Filter out non-Error unhandled rejections (Safari noise from extensions)
      // These have no stacktrace and no actionable information
      if (
        event.exception?.values?.some((e) => {
          const isUnhandledRejection =
            e.mechanism?.type === 'onunhandledrejection';
          if (!isUnhandledRejection) return false;

          return (
            e.value?.includes('Object captured as promise rejection') ||
            e.value?.includes('captured as promise rejection')
          );
        })
      ) {
        return null;
      }

      // Filter out errors thrown by scripts the browser injects into the page
      // (SMARTMIXIN-WEB-5J/5K: Chrome iOS on the reset-password form). Their
      // frames point at the document itself or at a script with no URL. Our
      // index.html has no inline script — all our code runs from /assets/ —
      // so an event where EVERY frame is like that cannot be ours. Events with
      // no frames at all are kept: no evidence either way. An injected script
      // keeps the URL it was injected at, so after an SPA navigation its
      // errors no longer match and still get through — noise, never a loss.
      if (isFromInjectedScript(event)) {
        return null;
      }

      return event;
    },
  });
};

export const createReduxEnhancer = () =>
  Sentry.createReduxEnhancer({
    actionTransformer: (action) => {
      if (action.type === 'user/storeToken') {
        // Return a transformed action to remove sensitive information
        return {
          type: action.type,
          payload: {},
        };
      }

      if (action.type.startsWith('staffElevation/')) {
        // The staff second-factor token (and the rejected one) travel in
        // these payloads: keep the breadcrumb, drop the credential.
        return {type: action.type};
      }

      return action;
    },
    stateTransformer: (state: RootState) => {
      // Transform the state to remove sensitive information
      const transformedState = {
        ...state,
        netatmoWeather: {
          ...state.netatmoWeather,
          historyData: null,
          reports: null,
        },
        user: {
          ...state.user,
          token: null,
        },
        staffElevation: {
          ...state.staffElevation,
          token: null,
        },
      };

      return transformedState;
    },
  });

// Function to set user context.
// Use this to send up info about the current logged in user
export const setUser = (email: string, username: string) => {
  if (process.env.NODE_ENV !== 'development') {
    Sentry.setUser({
      id: email,
      username,
      email,
    });
  }
};

// Function to report handled errors to Sentry
// I use this if I want to report some API failure
export const captureErrorMessage = (msg: string, tagCtx = {}) => {
  if (process.env.NODE_ENV !== 'development') {
    Sentry.setTags(tagCtx);
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      Sentry.captureMessage(msg);
    });
  } else {
    console.log(`[error] ${msg}`);
  }
};

export const captureWarningMessage = (msg: string, tagCtx = {}) => {
  if (process.env.NODE_ENV !== 'development') {
    Sentry.setTags(tagCtx);
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      Sentry.captureMessage(msg);
    });
  } else {
    console.log(`[warning] ${msg}`);
  }
};

export const captureInfoMessage = (msg: string, tagCtx = {}) => {
  if (process.env.NODE_ENV !== 'development') {
    Sentry.setTags(tagCtx);
    Sentry.withScope((scope) => {
      scope.setLevel('info');
      Sentry.captureMessage(msg);
    });
  } else {
    console.log(`[info] ${msg}`);
  }
};

export const captureException = () => {
  Sentry.captureException(new Error('Oops!'));
};
